import React, { useState, useEffect, useRef, useContext } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { eventAPI } from '../api';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { t } from '../i18n';

const supportedLocales = new Set([
  'ar', 'bg', 'ca', 'cs', 'de', 'el', 'en', 'es', 'fa', 'fi', 'fr', 'he',
  'hr', 'it', 'ja', 'ko', 'lt', 'no', 'pl', 'pt-BR', 'ro', 'ru', 'sk', 'sl',
  'sr', 'sv', 'tr', 'uz', 'vi', 'zh-Hans', 'zh-Hant'
]);

const getCurrentLanguage = () => {
  try {
    return localStorage.getItem('language') || navigator.language || 'en';
  } catch (e) {
    return navigator.language || 'en';
  }
};

const getLocale = () => {
  const language = getCurrentLanguage();
  if (supportedLocales.has(language)) return language;
  const baseLanguage = language.split('-')[0];
  return supportedLocales.has(baseLanguage) ? baseLanguage : 'en';
};

// small date helpers
const startOfWeek = (d) => {
  // start week on Monday
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7; // shift so Monday=0, Sunday=6
  dt.setHours(0,0,0,0);
  dt.setDate(dt.getDate() - day);
  return dt;
};
const addDays = (d, days) => { const x = new Date(d); x.setDate(x.getDate() + days); return x; };
const addMonths = (d, months) => { const x = new Date(d); x.setMonth(x.getMonth() + months); return x; };
const startOfMonth = (d) => { const dt = new Date(d); dt.setDate(1); dt.setHours(0,0,0,0); return dt; };
const endOfMonth = (d) => { const dt = new Date(d); dt.setMonth(dt.getMonth()+1); dt.setDate(0); dt.setHours(23,59,59,999); return dt; };

// Effective event end, with the calendar's existing one-hour fallback.
const getEventEndDate = (event, startDate = new Date(event.date_time)) => {
  if (event.ends_at) return new Date(event.ends_at);

  const duration = Number(event.duration_minutes);

  return new Date(
    startDate.getTime() + (duration > 0 ? duration : 60) * 60000
  );
};

// Clip an event to a local calendar day using half-open [start, end) intervals.
const getEventSegmentForDay = (event, day) => {
  const dayStart = new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate()
  );

  const dayEnd = addDays(dayStart, 1);

  const eventStart = new Date(event.date_time);
  const eventEnd = getEventEndDate(event, eventStart);

  // An event ending exactly at midnight does not belong to the next day.
  // An event starting exactly at midnight does not belong to the previous day.
  if (!(eventStart < dayEnd && eventEnd > dayStart)) return null;

  return {
    start: new Date(Math.max(eventStart.getTime(), dayStart.getTime())),
    end: new Date(Math.min(eventEnd.getTime(), dayEnd.getTime())),
    dayStart,
  };
};

// Determine event color based on user's relationship to the event
const getEventColor = (event, userId) => {
  // Check if user is the host
  if (event.creator && event.creator.id === userId) {
    return '#DAA520'; // Gold (distinct, saturated gold)
  }
  
  // Check user's response
  const userResponse = event.responses?.find(r => r.user?.id === userId);
  if (userResponse) {
    if (userResponse.response === 'will') {
      return '#28a745'; // Green
    } else if (userResponse.response === 'might') {
      return '#FF6B35'; // Vibrant orange-red
    } else if (userResponse.response === 'wont') {
      return '#6c757d'; // Gray
    }
  }

  // Not yet answered
  return '#007bff';
};

const Calendar = () => {
  const { user } = useContext(AuthContext);
  // Add page-specific class to <body> so global layout (floating-box) can
  // adapt per-page via CSS. This avoids hacks with negative margins.
  useEffect(() => {
    document.body.classList.add('page-calendar');
    return () => { document.body.classList.remove('page-calendar'); };
  }, []);
  const [events, setEvents] = useState([]);
  const lastSeenRef = useRef(new Date().toISOString());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const now = new Date();
  const todayDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const nowRounded = new Date(now);
  nowRounded.setSeconds(0,0);
  const defaultDuration = new Date(1970,0,1,1,0,0,0);

  const [formData, setFormData] = useState({
    title: '',
    date: todayDateOnly,
    time: nowRounded,
    duration: defaultDuration,
    place: '',
    place_name: '',
    place_address: '',
    place_lat: null,
    place_lng: null,
    description: '',
  });
  const [formError, setFormError] = useState('');
  const placeInputRef = useRef(null);
  const formCardRef = useRef(null);
  const lastSelectAtRef = useRef(0);
  const lastSelectedAddressRef = useRef(null);
  const lastSelectedClearRef = useRef(null);
  // Restore calendar view and selected date from localStorage so the user's
  // last-viewed mode/month is preserved when navigating away and back.
  const getInitialViewMode = () => {
    try {
      return localStorage.getItem('calendar:viewMode') || 'week';
    } catch (e) { return 'week'; }
  };
  const getInitialSelectedDate = () => {
    try {
      /*
      const v = localStorage.getItem('calendar:selectedDate');
      return v ? new Date(v) : new Date();
      */
     return new Date();
      } catch (e) { return new Date(); }
  };

  const [viewMode, setViewMode] = useState(getInitialViewMode);
  const [selectedDate, setSelectedDate] = useState(getInitialSelectedDate);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileTitle, setMobileTitle] = useState('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedDate]);

  // Persist viewMode and selectedDate so navigation away and back restores state
  useEffect(() => {
    try { localStorage.setItem('calendar:viewMode', viewMode); } catch (e) {}
  }, [viewMode]);

  /*
  useEffect(() => {
    try { localStorage.setItem('calendar:selectedDate', selectedDate.toISOString()); } catch (e) {}
  }, [selectedDate]);
  */
  // Listen for programmatic refresh requests (e.g., user clicked Calendar tab while already on calendar)
  useEffect(() => {
    const handler = () => {
      loadEvents();
    };
    window.addEventListener('calendar:refresh', handler);
    return () => window.removeEventListener('calendar:refresh', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEvents = async () => {
    try {
      // fetch all events and filter client-side for the selected period
      const response = await eventAPI.getAll();
      const all = response.data.map(ev => ({ ...ev, date_obj: new Date(ev.date_time) }));

      // update last seen based on server updated_at timestamps if present
      try {
        const maxUpdated = response.data.reduce((acc, ev) => {
          if (!ev.updated_at) return acc;
          const d = new Date(ev.updated_at);
          return d > acc ? d : acc;
        }, new Date(lastSeenRef.current));
        lastSeenRef.current = maxUpdated.toISOString();
      } catch (e) {
        lastSeenRef.current = new Date().toISOString();
      }

      let start, end;
      if (viewMode === 'week') {
        // Load buffer for mobile infinite scroll
        // Selected Date is start of "current" week. 
        // Mobile loads -1 week and +1 week relative to "current" (total 3 weeks displayed).
        // Let's load generous buffer: -14 days to +21 days from selected week start.
        const s = startOfWeek(selectedDate);
        start = addDays(s, -14);
        end = addDays(s, 21);
      } else {
        start = startOfMonth(selectedDate);
        end = endOfMonth(selectedDate);
      }

      const filtered = all.filter(ev => {
        const eventEnd = getEventEndDate(ev, ev.date_obj);
      
        return ev.date_obj < end && eventEnd > start;
      });
      
      setEvents(filtered);
    } catch (err) {
      console.error('Failed to load events:', err);
    }
  };

  // Poll for changes every 10 seconds and refresh if needed
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const since = lastSeenRef.current;
        const res = await eventAPI.getChanges(since);
        if (Array.isArray(res.data) && res.data.length > 0) {
          // reload full events for simplicity
          loadEvents();
        }
      } catch (err) {
        // ignore polling errors
      }
    }, 10000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedDate]); // Added dependencies so polling uses current view

  const showFormError = (msg) => {
    setFormError(msg);
    if (msg && formCardRef.current) {
      // wait a tick for layout then scroll the card into view
      setTimeout(() => {
        try { formCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
        // focus a helpful control: place input for place errors, otherwise first input
        setTimeout(() => {
          try {
            if (msg.toLowerCase().includes('place') && placeInputRef.current) {
              placeInputRef.current.focus();
            } else if (formCardRef.current) {
              const first = formCardRef.current.querySelector('.react-datepicker-input, input, textarea, select');
              if (first && typeof first.focus === 'function') first.focus();
            }
          } catch (e) {}
        }, 300);
      }, 50);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // If user edits the place text, clear structured place fields to force re-selection
    if (name === 'place') {
      setFormData({
        ...formData,
        place: value,
        place_name: '',
        place_address: '',
        place_lat: null,
        place_lng: null,
      });
      showFormError('');
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // date/time/duration handled as separate Date objects: formData.date, formData.time, formData.duration

  const handleDatePickerChange = (d) => setFormData(fd => ({ ...fd, date: d }));
  const handleTimePickerChange = (t) => setFormData(fd => ({ ...fd, time: t }));
  const handleDurationPickerChange = (dur) => setFormData(fd => ({ ...fd, duration: dur }));

  const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  // compute minTime for time picker: if date is today, min time is nowRounded
  const minTimeForTimePicker = isSameDay(formData.date, todayDateOnly) ? nowRounded : new Date(1970,0,1,0,0,0,0);
  const maxTimeForTimePicker = new Date(1970,0,1,23,45,0,0);

  const computeEndTime = () => {
    try {
      if (!formData.date || !formData.time || !formData.duration) return null;
      const d = formData.date;
      const t = formData.time;
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), t.getHours(), t.getMinutes(), 0, 0);
      const durMins = formData.duration.getHours()*60 + formData.duration.getMinutes();
      if (durMins <= 0) return null;
      const end = new Date(start.getTime() + durMins * 60000);
      return end.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });
    } catch (err) {
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Place is optional, but if entered it must come from a suggestion
    const hasPlaceText = Boolean(formData.place.trim());
    const hasSelectedPlace =
      formData.place_lat !== null &&
      formData.place_lng !== null;

    if (hasPlaceText && !hasSelectedPlace) {
      showFormError(t('calendarSelectPlaceSuggestion'));
      return;
    }

    if (!formData.date || !formData.time) {
      showFormError(t('calendarSelectDateAndTime'));
      return;
    }

    if (!formData.duration || ((formData.duration.getHours()*60 + formData.duration.getMinutes()) <= 0)) {
      showFormError(t('calendarPositiveDuration'));
      return;
    }

    try {
      // combine date + time into a local Date then convert to ISO for API
      const d = formData.date;
      const t = formData.time;
      const localDt = new Date(d.getFullYear(), d.getMonth(), d.getDate(), t.getHours(), t.getMinutes(), 0, 0);
      // Prevent creating events in the past
      const nowLocal = new Date();
      if (localDt <= nowLocal) {
        showFormError(t('calendarFutureDateRequired'));
        return;
      }
      const durationMinutes = formData.duration.getHours()*60 + formData.duration.getMinutes();
      const payload = {
        title: formData.title,
        date_time: localDt.toISOString(),
        place: formData.place,
        place_name: formData.place_name,
        place_address: formData.place_address,
        place_lat: formData.place_lat,
        place_lng: formData.place_lng,
        description: formData.description,
        duration_minutes: Number(durationMinutes),
      };
      await eventAPI.create(payload);
      setShowCreateForm(false);
      setFormData({
        title: '',
        date: todayDateOnly,
        time: nowRounded,
        duration: defaultDuration,
        place: '',
        place_name: '',
        place_address: '',
        place_lat: null,
        place_lng: null,
        description: '',
      });
      showFormError('');
      loadEvents();
    } catch (err) {
      console.error('Failed to create event:', err);
      // Display backend error message if available
      if (err.response && err.response.data && err.response.data.error) {
        showFormError(err.response.data.error);
      } else {
        showFormError(t('calendarCreateEventFailed'));
      }
    }
  };

  const [suggestions, setSuggestions] = React.useState([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [suppressSuggestions, setSuppressSuggestions] = React.useState(false);
  const suppressTimeoutRef = useRef(null);

  // Nominatim (OpenStreetMap) autocomplete: debounce user input and query Nominatim.
  useEffect(() => {
    if (!showCreateForm) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (suppressSuggestions) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const q = formData.place && formData.place.trim();
    if (!q || q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        // if we recently selected a suggestion, skip doing a new query for a short window
        if (lastSelectAtRef.current && (Date.now() - lastSelectAtRef.current) < 700) {
          return;
        }
        // if the query exactly matches the last selected address, skip querying to avoid re-showing the dropdown
        if (lastSelectedAddressRef.current && q === lastSelectedAddressRef.current) {
          return;
        }
        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(q)}&limit=8`;
        const res = await fetch(url, { signal: controller.signal, headers: { 'Accept-Language': getLocale() } });
        if (!res.ok) return;
        const data = await res.json();
        setSuggestions(data.map(item => ({
          id: item.place_id,
          display_name: item.display_name,
          // some responses include namedetails, fall back to first comma-separated part
          name: (item.namedetails && item.namedetails.name) ? item.namedetails.name : item.display_name.split(',')[0],
          lat: item.lat,
          lon: item.lon,
          address: item.display_name,
        })));
        setShowSuggestions(true);
      } catch (err) {
        if (err.name !== 'AbortError') console.warn('Nominatim search failed', err);
      }
    }, 300);

    return () => { controller.abort(); clearTimeout(timeout); };
  }, [formData.place, showCreateForm, suppressSuggestions]);

  const handleSelectSuggestion = (s) => {
    setFormData(fd => ({
      ...fd,
      // show the full resolved address in the input when selected
      place: s.address,
      place_name: s.name || s.display_name,
      place_address: s.address,
      // round lat/lng to 6 decimal places to satisfy backend DecimalField
      place_lat: s.lat ? Number(parseFloat(s.lat).toFixed(6)) : null,
      place_lng: s.lon ? Number(parseFloat(s.lon).toFixed(6)) : null,
    }));
    setSuggestions([]);
    setShowSuggestions(false);
    if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);
    setSuppressSuggestions(true);
    lastSelectAtRef.current = Date.now();
    // remember the exact address selected so we don't immediately re-query for it
    lastSelectedAddressRef.current = s.address;
    if (lastSelectedClearRef.current) clearTimeout(lastSelectedClearRef.current);
    lastSelectedClearRef.current = setTimeout(() => { lastSelectedAddressRef.current = null; lastSelectedClearRef.current = null; }, 2500);
    suppressTimeoutRef.current = setTimeout(() => setSuppressSuggestions(false), 600);
  };

  // cleanup suppress timeout on unmount
  useEffect(() => {
    return () => {
      if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);
    };
  }, []);

  return (
    <div className="container">
      <div className="calendar-top-row">
        <div className="calendar-control-box top-left">
          <button className="btn btn-primary" onClick={() => setShowCreateForm(!showCreateForm)}>
            {showCreateForm ? t('cancel') : t('createNewEvent')}
          </button>
        </div>

        <div className="calendar-top-center">
          {!isMobile && <h1>{t('calendar')}</h1>}
        </div>

        <div className="calendar-control-box top-right" style={{ justifyContent: 'flex-end' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={`btn ${viewMode === 'week' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setViewMode('week')}>{t('week')}</button>
            {!isMobile && <button className={`btn ${viewMode === 'month' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setViewMode('month')}>{t('month')}</button>}
          </div>
        </div>
      </div>

      {showCreateForm && (
        <div className="card" ref={formCardRef}>
          <h3>{t('createNewEvent')}</h3>
          {formError && <div style={{ color: 'red', marginBottom: '10px' }}>{formError}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{t('eventTitle')} *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                maxLength={100}
              />
            </div>
            {/* Activity field removed — no longer needed */}
            <div className="form-group">
              <label>{t('date')} *</label>
              <ReactDatePicker
                selected={formData.date}
                onChange={handleDatePickerChange}
                dateFormat="dd/MM/yyyy"
                className="react-datepicker-input"
                required
              />
            </div>
            <div className="form-group">
              <label>{t('time')} *</label>
              <ReactDatePicker
                selected={formData.time}
                onChange={handleTimePickerChange}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeFormat="HH:mm"
                dateFormat="HH:mm"
                className="react-datepicker-input"
                minTime={minTimeForTimePicker}
                maxTime={maxTimeForTimePicker}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('duration')} *</label>
              <ReactDatePicker
                selected={formData.duration}
                onChange={handleDurationPickerChange}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeFormat="HH:mm"
                timeCaption={t('duration')}
                dateFormat="HH:mm"
                className="react-datepicker-input"
                minTime={new Date(1970,0,1,0,15)}
                maxTime={new Date(1970,0,1,23,45)}
                required
              />
              {(() => {
                const endStr = computeEndTime();
                return endStr ? (
                  <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>{t('endsAt')}: {endStr}</div>
                ) : null;
              })()}
            </div>
            <div className="form-group">
              <label>{t('place')}</label>
              <input
                type="text"
                name="place"
                ref={placeInputRef}
                value={formData.place}
                onChange={handleChange}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onFocus={() => { if (suggestions.length > 0 && !suppressSuggestions) setShowSuggestions(true); }}
                placeholder={t('searchForPlace')}
              />
              {showSuggestions && suggestions.length > 0 && (
                <ul className="autocomplete-list">
                  {suggestions.map(s => (
                    <li key={s.id} className="autocomplete-item" onMouseDown={() => handleSelectSuggestion(s)}>
                      <div style={{ fontWeight: 600 }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>{s.address}</div>
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>{t('placeSearchTip')}</div>
            </div>
            <div className="form-group">
              <label>{t('description')}</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                maxLength={500}
              />
            </div>
            <button type="submit" className="btn btn-primary">{t('createEvent')}</button>
          </form>
        </div>
      )}

      <div>
        {/* Title row with Today on left, title centered, Prev/Next on right */}
        <div className="calendar-header-row">
          <div className="calendar-control-box left" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-secondary" onClick={() => setSelectedDate(new Date())}>{t('today')}</button>
            {isMobile && viewMode === 'month' && (
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedDate(addMonths(selectedDate, -1))}
                aria-label={t('previousMonth')}
              >
                <span style={{ display: 'inline-block', transform: 'scale(1.4)', transformOrigin: 'center' }}>‹</span>
              </button>
            )}
          </div>

          <div className="calendar-title-wrap">
            {viewMode === 'month' ? (
              <div className="calendar-title">
                {selectedDate.toLocaleDateString(getLocale(), { month: 'long', year: 'numeric' })}
              </div>
            ) : (
              (() => {
                if (isMobile && mobileTitle) return <div className="calendar-title">{mobileTitle}</div>;
                const start = startOfWeek(selectedDate);
                const end = addDays(start, 6);
                return (
                  <div className="calendar-title">
                    {start.toLocaleDateString(getLocale(), { month: 'short', day: 'numeric' })} — {end.toLocaleDateString(getLocale(), { month: 'short', day: 'numeric' })}
                  </div>
                );
              })()
            )}
          </div>

          <div className="calendar-control-box right" style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
            {isMobile ? (
              <>
                {viewMode === 'month' && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedDate(addMonths(selectedDate, 1))}
                    aria-label={t('nextMonth')}
                  >
                    <span style={{ display: 'inline-block', transform: 'scale(1.4)', transformOrigin: 'center' }}>›</span>
                  </button>
                )}
                <button className={`btn ${viewMode === 'month' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setViewMode('month')}>{t('month')}</button>
              </>
            ) : (
              <div className="mobile-hide" style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => setSelectedDate(addDays(selectedDate, viewMode === 'week' ? -7 : -30))}>{t('previous')}</button>
                <button className="btn btn-secondary" onClick={() => setSelectedDate(addDays(selectedDate, viewMode === 'week' ? 7 : 30))}>{t('next')}</button>
              </div>
            )}
          </div>
        </div>

        {viewMode === 'week' ? (
          <WeekView events={events} selectedDate={selectedDate} navigate={navigate} userId={user?.id} setSelectedDate={setSelectedDate} setMobileTitle={setMobileTitle} />
        ) : (
          <MonthView events={events} selectedDate={selectedDate} navigate={navigate} userId={user?.id} />
        )}
      </div>
    </div>
  );
};

export default Calendar;

const hours = Array.from({length: 24}, (_,i) => i); // 00:00 - 23:00

// Merge repost copies by `event_id` so reposts act as a single event on the frontend.
const mergeCopiesList = (list, userId) => {
  const map = new Map();
  list.forEach(ev => {
    const key = ev.event_id || ev.copy_id || ev.id;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(ev);
  });

  const merged = [];
  for (const copies of map.values()) {
    // choose a representative copy: prefer a copy owned by current user, otherwise first copy
    let chosen = copies[0];
    // if user is one of the hosts, prefer that copy
    const myHost = copies.find(c => c.creator && c.creator.id === userId);
    if (myHost) chosen = myHost;

      // combine responses across copies so getEventColor() can find the viewer's response
      const combinedResponses = [].concat(...copies.map(c => c.responses || []));

      // aggregate visible hosts (serializer returns hosts filtered by friendship)
      const combinedHosts = Array.from(new Set([].concat(...copies.map(c => c.hosts || []))));

      const mergedEv = { ...chosen, responses: combinedResponses, hosts: combinedHosts, copies };
    merged.push(mergedEv);
  }

  return merged;
};

const WeekView = ({ events, selectedDate, navigate, userId, setSelectedDate, setMobileTitle }) => {
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // For mobile infinite scroll
  const [mobileStartDate, setMobileStartDate] = useState(() => startOfWeek(selectedDate));
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMobileStartDate(startOfWeek(selectedDate));
  }, [selectedDate]);

  const daysToRenderCount = isMobile ? 21 : 7;
  const renderStartDay = isMobile ? addDays(mobileStartDate, -7) : mobileStartDate;
  
  const days = Array.from({length: daysToRenderCount}, (_,i) => addDays(renderStartDay, i));

  // group events by day
  const byDay = days.map(d =>
    events.filter(ev => getEventSegmentForDay(ev, d) !== null)
  );

  const mergedByDay = byDay.map(list => mergeCopiesList(list, userId));
  
  const weekGridRef = useRef(null);

  // Auto-scroll to the "current" week on initial load for mobile
  useEffect(() => {
    if (isMobile && weekGridRef.current) {
      // Days are index 0..20. The 'current' week starts at index 7.
      // We want to align the view to 'selectedDate'.
      // Calculate offset of selectedDate from mobileStartDate (which is index 7)
      const diffTime = selectedDate.getTime() - mobileStartDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      // Clamp offset between 0 and 6 (within the central week) to be safe
      const offset = Math.max(0, Math.min(6, diffDays));
      const targetIndex = 7 + offset;

      const totalW = weekGridRef.current.scrollWidth;
      const daysW = totalW - 40;
      const dayWidth = daysW / daysToRenderCount;
      weekGridRef.current.scrollLeft = Math.round(dayWidth * targetIndex);
    }
  }, [isMobile, mobileStartDate, selectedDate, daysToRenderCount]); 

  const [gridHeight, setGridHeight] = useState('60vh');
  
  // Calculate dynamic height to fill available space in floating box
  useEffect(() => {
    const updateHeight = () => {
      const gridEl = weekGridRef.current;
      const floatingBox = document.querySelector('.floating-box');
      
      if (gridEl && floatingBox) {
        const boxRect = floatingBox.getBoundingClientRect();
        const gridRect = gridEl.getBoundingClientRect();
        
        // Calculate available vertical space: Box Bottom - Grid Top
        // Subtract a small buffer (e.g. 10px or padding) to avoid scroll-in-scroll conflict
        const available = boxRect.bottom - gridRect.top - 24;
        
        // Ensure a reasonable minimum height
        const newHeight = Math.max(200, available);
        setGridHeight(`${newHeight}px`);
      }
    };
    
    // Run initially and on resize
    updateHeight();
    // Also run after a short delay since layout might shift
    setTimeout(updateHeight, 100);
    
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []); // Run once on mount

  const [visibleTitle, setVisibleTitle] = useState('');
  
  const [visibleRange, setVisibleRange] = useState({ top: 0, bottom: 0 });

  const handleGridScroll = () => {
    const grid = weekGridRef.current;
    if (!grid) return;

    // --- Vertical Visibility Logic ---
    const scrollTop = grid.scrollTop;
    const viewportHeight = grid.clientHeight;
    // Sticky header spacer logic (54px)
    const topHour = scrollTop / 40; // hourRowHeight
    const bottomHour = (scrollTop + viewportHeight - 54) / 40; 
    setVisibleRange({ top: topHour, bottom: bottomHour });

    // --- Horizontal Infinite Scroll Logic (Mobile) ---
    if (isMobile) {
      const scrollLeft = grid.scrollLeft;
      const totalW = grid.scrollWidth;
      const daysW = totalW - 40; // subtract time column
      if (daysW <= 0) return;
      const dayWidth = daysW / daysToRenderCount;
      
      // Index of day immediately to the right of the Time Column
      const firstVisibleIndex = Math.round(scrollLeft / dayWidth);
      
      if (firstVisibleIndex >= 0 && firstVisibleIndex < daysToRenderCount) {
        const firstDay = days[firstVisibleIndex];
        const lastDay = days[Math.min(firstVisibleIndex + 2, daysToRenderCount - 1)];
        
        // Update title
        if (firstDay && lastDay) {
          if (firstDay.getMonth() === lastDay.getMonth()) {
            setVisibleTitle(firstDay.toLocaleDateString(getLocale(), { month: 'long' }));
          } else {
            const m1 = firstDay.toLocaleDateString(getLocale(), { month: 'short' });
            const m2 = lastDay.toLocaleDateString(getLocale(), { month: 'short' });
            setVisibleTitle(`${m1}/${m2}`);
          }
        }

        // Infinite Scroll Thresholds
        if (firstVisibleIndex > daysToRenderCount - 6) { 
           // Near end -> shift forward
           const nextWeek = addDays(mobileStartDate, 7);
           setMobileStartDate(nextWeek);
           if (setSelectedDate) setSelectedDate(nextWeek);
        }
        if (firstVisibleIndex < 3) {
           // Near start -> shift back
           const prevWeek = addDays(mobileStartDate, -7);
           setMobileStartDate(prevWeek);
           if (setSelectedDate) setSelectedDate(prevWeek);
        }
      }
    }
  };

  // Sync title via React state in parent instead of direct DOM writes
  useEffect(() => {
    if (setMobileTitle && isMobile) setMobileTitle(visibleTitle || '');
    return () => { if (setMobileTitle && isMobile) setMobileTitle(''); };
  }, [visibleTitle, isMobile, setMobileTitle]);

  /* ... reuse helpers ... */
  const slotTop = (date) => {
    const h = date.getHours() + date.getMinutes()/60;
    return (h / 24) * 100; 
  };
  const slotHeight = (startDt, endDt) => {
    const diff = (endDt - startDt) / (1000*60*60);
    return (diff / 24) * 100; 
  };

  const hourRowHeight = 40; 
  const timelineMinHeight = hours.length * hourRowHeight; 
  const spacerHeight = 54; 

  // Initial vertical scroll
  useEffect(() => {
    const target = spacerHeight + hourRowHeight * 8; 
    setTimeout(() => {
      if (weekGridRef.current) {
        weekGridRef.current.scrollTop = target;
        // Trigger initial calculation
        handleGridScroll();
      }
    }, 100);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ... Interaction State ... */
  const [hoveredId, setHoveredId] = useState(null);
  const [pinnedId, setPinnedId] = useState(null);
  const [expandedInfo, setExpandedInfo] = useState({ id: null, percent: 0 });
  const [hoverExpandedId, setHoverExpandedId] = useState(null);
  const dayBodyRefs = useRef([]);
  const eventRefs = useRef({});

  // Document click handler for collapse
  useEffect(() => {
    const handleDocClick = (e) => {
      if (!expandedInfo || !expandedInfo.id) return;
      if (e.target && e.target.closest && e.target.closest('.event-item')) return;
      setExpandedInfo({ id: null, percent: 0 });
    };
    document.addEventListener('click', handleDocClick);
    return () => document.removeEventListener('click', handleDocClick);
  }, [expandedInfo]);

  const getColorPriority = (color) => {
    if (color === '#DAA520') return 5; 
    if (color === '#28a745') return 4;
    if (color === '#FF6B35') return 3; 
    if (color === '#007bff') return 2; 
    if (color === '#6c757d') return 1; 
    return 0;
  };

  const getOffScreenIndicators = (dayEvents, day) => {
    let topColor = null;
    let bottomColor = null;
    let topPriority = 0;
    let bottomPriority = 0;
  
    dayEvents.forEach(ev => {
      const segment = getEventSegmentForDay(ev, day);
  
      if (!segment) return;
  
      const startHour =
        (segment.start.getTime() - segment.dayStart.getTime()) / 3600000;
  
      const endHour =
        (segment.end.getTime() - segment.dayStart.getTime()) / 3600000;
  
      const color = getEventColor(ev, userId);
      const priority = getColorPriority(color);
  
      if (endHour <= visibleRange.top) {
        if (priority > topPriority) {
          topPriority = priority;
          topColor = color;
        }
      }
  
      if (startHour >= visibleRange.bottom) {
        if (priority > bottomPriority) {
          bottomPriority = priority;
          bottomColor = color;
        }
      }
    });
  
    return { topColor, bottomColor };
  };

  return (
    <div className="calendar">
      {/* Scrollable timeline area (Unified Grid) */}
      <div 
        ref={weekGridRef} 
        onScroll={handleGridScroll}
        className={isMobile ? "mobile-unified-scroll" : "week-grid"} 
        style={{ display: 'flex', alignItems: 'flex-start', width: '100%', height: gridHeight, overflowY: 'auto' }}
      >
        <div className={`time-column ${isMobile ? 'time-column-mobile-sticky' : ''}`} style={{ flex: '0 0 40px' }}>
          <div style={{ position: 'sticky', top: 0, height: 54, width: '100%', background: '#fff', zIndex: 20 }}></div>
          {hours.map(h => <div key={h} className="time-cell" style={{ height: 40, flexShrink: 0 }}>{h.toString().padStart(2, '0')}:00</div>)}
        </div>

        <div 
          className={isMobile ? "mobile-display-contents" : "days-columns"}
          style={isMobile ? {} : { display: 'flex', flex: 1, gap: 8 }}
        >
          {days.map((d, idx) => {
            const dayEventsList = mergedByDay[idx];
            let maxOverlap = 0;
            if (dayEventsList.length > 0) {
              const points = [];
            
              dayEventsList.forEach(ev => {
                const segment = getEventSegmentForDay(ev, d);
            
                if (!segment) return;
            
                const startMin =
                  (segment.start.getTime() - segment.dayStart.getTime()) / 60000;
            
                const endMin =
                  (segment.end.getTime() - segment.dayStart.getTime()) / 60000;
            
                points.push([startMin, 1]);
                points.push([endMin, -1]);
              });
            
              points.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
            
              let cur = 0;
            
              for (const p of points) {
                cur += p[1];
                if (cur > maxOverlap) maxOverlap = cur;
              }
            }
            
            const flexStyle = isMobile ? { } : { flex: `${1 + Math.max(0, maxOverlap)} 1 0`, minWidth: 60 };
            const mobileClass = isMobile ? "mobile-day-snap-item" : "";
            const { topColor, bottomColor } = getOffScreenIndicators(mergedByDay[idx], d);
            
              return (
              <div 
                key={d.toISOString()} 
                className={`day-column ${mobileClass}`} 
                style={{ 
                  ...flexStyle, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  overflow: 'visible', 
                  background: isMobile ? 'transparent' : '#f0f0f0', 
                  // Fix: Remove borders on PC/Mobile for cleaner look, padding maintained
                  border: 'none', 
                  borderRight: 'none',
                  position: 'relative',
                  padding: isMobile ? '0 2px' : '0' // Breathing room!
                }}
              >
                <div style={{ position: 'sticky', top: 0, zIndex: 2100, backgroundColor: '#fff', paddingBottom: 10 }}>
                  <div className="day-header" style={{
                    height: 44, 
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: '#f0f0f0', border: '1px solid #eee', borderRadius: 6, boxSizing: 'border-box', fontWeight: 600 
                  }}>
                    {d.toLocaleDateString(getLocale(), { weekday: 'short', day: 'numeric' })}
                  </div>
                  {topColor && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 12px)', left: '50%', transform: 'translateX(-50%) rotate(135deg)', width: 8, height: 8, borderLeft: `2px solid ${topColor}`, borderBottom: `2px solid ${topColor}`, zIndex: 11 }}></div>
                  )}
                </div>
                        <div ref={el => { dayBodyRefs.current[idx] = el; }} className="day-body" style={{ position: 'relative', minHeight: `${timelineMinHeight}px`, backgroundImage: 'none', boxSizing: 'border-box', backgroundColor: '#f0f0f0', border: '1px solid #eee', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: `${timelineMinHeight}px`, backgroundImage: 'repeating-linear-gradient(to bottom, transparent, transparent 39px, #ddd 39px, #ddd 40px)', zIndex: 0, pointerEvents: 'none' }} />
                  {
                    (() => {
                      // ... Layout Logic (unchanged essentially) ...
                      const metas = dayEventsList.map(ev => {
                        const segment = getEventSegmentForDay(ev, d);
                      
                        return {
                          ev,
                          startMin: (segment.start.getTime() - segment.dayStart.getTime()) / 60000,
                          endMin: (segment.end.getTime() - segment.dayStart.getTime()) / 60000,
                          startDt: segment.start,
                          endDt: segment.end,
                        };
                      }).sort((a, b) => a.startMin - b.startMin);
                      let active = [];
                      metas.forEach(m => { active = active.filter(x => x.endMin > m.startMin); m.stackIndex = active.length; active.push(m); });
                      const maxStack = metas.reduce((acc, m) => Math.max(acc, (m.stackIndex || 0) + 1), 0) || 1;
                      
                      return metas.map(meta => {
                        const { ev, startDt, endDt } = meta;
                        const top = slotTop(startDt);
                        const height = slotHeight(startDt, endDt);
                        const bgColor = getEventColor(ev, userId);
                        const stackIndex = meta.stackIndex || 0;
                        const offsetX = stackIndex * 8;
                        const rightOffset = (maxStack - 1 - stackIndex) * 8;
                        const isPinned = pinnedId === ev.id;
                        const isExpanded = expandedInfo.id === ev.id;
                        const z = (isPinned || hoveredId === ev.id) ? 1000 : 200 + stackIndex;

                        return (
                          <div
                            key={ev.id}
                            ref={el => { if (el) eventRefs.current[ev.id] = el; }}
                            className={`event-item${isExpanded ? ' event-expanded' : ''}`}
                            style={{ top: `${top}%`, height: `${isExpanded ? Math.max(height, expandedInfo.percent) : Math.max(1, height)}%`, backgroundColor: bgColor, left: `${6+offsetX}px`, right: `${6+rightOffset}px`, zIndex: z, overflow: isExpanded?'visible':'hidden' }}
                            onMouseEnter={() => {
                               setHoveredId(ev.id);
                               const el = eventRefs.current[ev.id];
                               const container = dayBodyRefs.current[idx];
                               if (el && container && el.scrollHeight > el.clientHeight + 2) {
                                  const needed = Math.min(100, (el.scrollHeight / container.clientHeight) * 100);
                                  setExpandedInfo({ id: ev.id, percent: Math.max(height, needed) });
                                  setHoverExpandedId(ev.id);
                               }
                            }}
                            onMouseLeave={() => {
                              setHoveredId(null);
                              if (hoverExpandedId === ev.id) { setExpandedInfo({ id: null, percent: 0 }); setHoverExpandedId(null); }
                            }}
                            onClick={(e) => {
                                // ... Same Click Logic ... 
                                const el = eventRefs.current[ev.id];
                                const container = dayBodyRefs.current[idx];
                                const isTruncated = el && container && (el.scrollHeight > el.clientHeight + 2);
                                if (expandedInfo.id === ev.id) { navigate(`/event/${ev.copy_id || ev.id}`); return; }
                                if (!expandedInfo.id && isTruncated) {
                                  const needed = Math.min(100, (el.scrollHeight / container.clientHeight) * 100);
                                  setExpandedInfo({ id: ev.id, percent: Math.max(height, needed) });
                                  return;
                                }
                                if (!isTruncated && maxStack <= 1) { navigate(`/event/${ev.copy_id || ev.id}`); return; }
                                if (pinnedId === ev.id || hoveredId === ev.id) { navigate(`/event/${ev.copy_id || ev.id}`); } else { setPinnedId(ev.id); }
                            }}
                          >
                           <div className="event-title">{ev.title}</div>
                            <div className="event-time">
                              {ev.hosts && ev.hosts.length ? <div className="event-hosts">{ev.hosts.join(', ')}</div> : null}
                              <div>{startDt.toLocaleTimeString(getLocale(), {hour: '2-digit', minute:'2-digit'})}{' -> '}{endDt.toLocaleTimeString(getLocale(), {hour: '2-digit', minute:'2-digit'})}</div>
                            </div>
                          </div>
                        );
                      });
                    })()
                  }
                </div>
                <div style={{ position: 'sticky', bottom: 20, zIndex: 11, height: 0 }}>
                  {bottomColor && (
                    <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%) rotate(-45deg)', width: 8, height: 8, borderLeft: `2px solid ${bottomColor}`, borderBottom: `2px solid ${bottomColor}` }}></div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MonthView({ events, selectedDate, navigate, userId }) {
  const [hoveredDay, setHoveredDay] = useState(null);
  const start = startOfMonth(selectedDate);
  const firstWeekStart = startOfWeek(start);
  const days = [];
  for (let i=0;i<42;i++) days.push(addDays(firstWeekStart, i));

  return (
    <div className="calendar month-view">
      <div className="month-grid">
        {[t('mondayShort'),t('tuesdayShort'),t('wednesdayShort'),t('thursdayShort'),t('fridayShort'),t('saturdayShort'),t('sundayShort')].map(d => <div key={d} className="month-day-name">{d}</div>)}
        {days.map((d, idx) => {
          const rawDayEvents = events.filter(ev => {
            const dt = new Date(ev.date_time);
            return dt.getFullYear() === d.getFullYear() && dt.getMonth() === d.getMonth() && dt.getDate() === d.getDate();
          });
          const mergedDayEvents = mergeCopiesList(rawDayEvents, userId);
          const muted = d.getMonth() !== selectedDate.getMonth();
          const hasMore = mergedDayEvents.length > 3;
          
          return (
            <div 
              key={d.toISOString()} 
              className={`month-cell ${muted ? 'muted' : ''}`}
              onMouseEnter={() => hasMore && setHoveredDay(idx)}
              onMouseLeave={() => setHoveredDay(null)}
              onClick={(e) => {
                // If the cell has hidden events, toggle the popup when tapping/clicking empty area (mobile support)
                if (hasMore) {
                  setHoveredDay(prev => prev === idx ? null : idx);
                }
              }}
              style={{ position: 'relative' }}
            >
              <div className="cell-number">{d.getDate()}</div>
              <div className="cell-events">
                {mergedDayEvents.slice(0,3).map(ev => {
                  const bgColor = getEventColor(ev, userId);
                  return (
                    <div key={ev.id} className="month-event" style={{ backgroundColor: bgColor }} onClick={(e) => { e.stopPropagation(); navigate(`/event/${ev.copy_id || ev.id}`); }}>{ev.title}</div>
                  );
                })}
                {hasMore && <div className="more">+{mergedDayEvents.length-3} {t('more')}</div>}
              </div>
              
              {/* Tooltip showing all events on hover */}
              {hoveredDay === idx && hasMore && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  padding: '8px',
                  zIndex: 1000,
                  minWidth: '200px',
                  maxWidth: '300px',
                }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '6px', color: '#666' }}>
                    {d.toLocaleDateString(getLocale(), { month: 'short', day: 'numeric' })}
                  </div>
                  {mergedDayEvents.map(ev => {
                    const bgColor = getEventColor(ev, userId);
                    return (
                      <div 
                        key={ev.id} 
                        style={{ 
                          backgroundColor: bgColor,
                          color: 'white',
                          padding: '4px 8px',
                          marginBottom: '4px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/event/${ev.copy_id || ev.id}`);
                        }}
                      >
                        {ev.title}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
